import React, { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';

// DL.2 - Αντίγραφο ιατρικού φακέλου νοσηλείας θανόντος
const DL2 = ({ formData, updateFormData }) => {
  const familyCertRef = useRef(null);
  const willCertRef = useRef(null);
  const [dragActive, setDragActive] = useState({ family: false, will: false });

  const handleChange = (e) => {
    updateFormData({ [e.target.name]: e.target.value });
  };

  const validateAMKA = (amka) => {
    return /^\d{11}$/.test(amka);
  };

  const handleAMKAChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 11) {
      updateFormData({ patientAmka: value });
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 15) {
      updateFormData({ phone: value });
    }
  };

  const handleDrag = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(prev => ({ ...prev, [type]: true }));
    } else if (e.type === "dragleave") {
      setDragActive(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleFile = (file, fieldName) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!allowedTypes.includes(file.type)) {
      alert('Το αρχείο δεν είναι υποστηριζόμενου τύπου. Επιτρέπονται μόνο JPG, PNG και PDF.');
      return;
    }
    
    if (file.size > maxSize) {
      alert('Το αρχείο είναι πολύ μεγάλο. Μέγιστο μέγεθος: 10MB.');
      return;
    }

    updateFormData({ [fieldName]: file });
  };

  return (
    <div>
      <div className="alert" style={{ 
        background: '#fff3cd', 
        borderLeft: '4px solid #ffc107', 
        padding: '15px',
        marginBottom: '20px'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>
          Σας γνωρίζουμε ότι σύμφωνα με την κείμενη νομοθεσία (ΚΙΔ) για πρόσβαση σε ιατρικά δεδομένα θανόντος απαιτούνται τα κάτωθι στοιχεία και δικαιολογητικά:
        </p>
      </div>

      <div className="form-group">
        <label htmlFor="patientAmka">
          ΑΜΚΑ ασθενή <span className="required">*</span>
        </label>
        <input
          type="text"
          id="patientAmka"
          name="patientAmka"
          value={formData.patientAmka || ''}
          onChange={handleAMKAChange}
          placeholder="12345678901"
          maxLength="11"
          required
        />
        {formData.patientAmka && !validateAMKA(formData.patientAmka) && (
          <small style={{ color: '#e74c3c', display: 'block', marginTop: '5px' }}>
            Το AMKA πρέπει να είναι ακριβώς 11 ψηφία
          </small>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="periodOfHospitalization">
          Περίοδος νοσηλείας <span className="required">*</span>
        </label>
        <input
          type="text"
          id="periodOfHospitalization"
          name="periodOfHospitalization"
          value={formData.periodOfHospitalization || ''}
          onChange={handleChange}
          placeholder="π.χ. 15/01/2024 - 20/01/2024"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="clinic">
          Κλινική νοσηλείας
        </label>
        <input
          type="text"
          id="clinic"
          name="clinic"
          value={formData.clinic || ''}
          onChange={handleChange}
          placeholder="π.χ. Παθολογική Κλινική"
        />
      </div>

      {/* Πιστοποιητικό Συγγενών */}
      <div className="form-group">
        <label>
          Πιστοποιητικό Εγγυτέρων Συγγενών <span className="required">*</span>
        </label>
        <div
          className={`file-upload-area ${dragActive.family ? 'dragover' : ''}`}
          onDragEnter={(e) => handleDrag(e, 'family')}
          onDragLeave={(e) => handleDrag(e, 'family')}
          onDragOver={(e) => handleDrag(e, 'family')}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(prev => ({ ...prev, family: false }));
            if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0], 'familyCertificate');
          }}
          onClick={() => familyCertRef.current?.click()}
        >
          <Upload className="file-upload-icon" size={40} />
          <div className="file-upload-text">Επισυνάψτε το αρχείο εδώ</div>
          <div className="file-upload-hint">PDF, JPG (max 10MB)</div>
          <input
            ref={familyCertRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={(e) => e.target.files[0] && handleFile(e.target.files[0], 'familyCertificate')}
            style={{ display: 'none' }}
          />
        </div>
        {formData.familyCertificate && (
          <div style={{ marginTop: '10px', color: '#27ae60', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>✓ {formData.familyCertificate.name}</span>
            <button
              type="button"
              onClick={() => updateFormData({ familyCertificate: null })}
              style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Βεβαίωση Διαθήκης */}
      <div className="form-group">
        <label>
          Βεβαίωση περί μη Δημοσίευσης Διαθήκης ή Αντίγραφο Διαθήκης <span className="required">*</span>
        </label>
        <div
          className={`file-upload-area ${dragActive.will ? 'dragover' : ''}`}
          onDragEnter={(e) => handleDrag(e, 'will')}
          onDragLeave={(e) => handleDrag(e, 'will')}
          onDragOver={(e) => handleDrag(e, 'will')}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(prev => ({ ...prev, will: false }));
            if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0], 'willCertificate');
          }}
          onClick={() => willCertRef.current?.click()}
        >
          <Upload className="file-upload-icon" size={40} />
          <div className="file-upload-text">Επισυνάψτε το αρχείο εδώ</div>
          <div className="file-upload-hint">PDF, JPG (max 10MB)</div>
          <input
            ref={willCertRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={(e) => e.target.files[0] && handleFile(e.target.files[0], 'willCertificate')}
            style={{ display: 'none' }}
          />
        </div>
        {formData.willCertificate && (
          <div style={{ marginTop: '10px', color: '#27ae60', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>✓ {formData.willCertificate.name}</span>
            <button
              type="button"
              onClick={() => updateFormData({ willCertificate: null })}
              style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="email">
            Email <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email || ''}
            onChange={handleChange}
            placeholder="example@email.com"
            required
          />
          <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
            Το ίδιο με αυτό που δηλώσατε στην ΥΔ ή Εξουσιοδότηση
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="phone">
            Τηλ. Επικοινωνίας <span className="required">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone || ''}
            onChange={handlePhoneChange}
            placeholder="2101234567"
            inputMode="numeric"
            pattern="\d{10,15}"
            title="Εισάγετε μόνο αριθμούς (10-15 ψηφία)"
            required
          />
        </div>
      </div>
    </div>
  );
};

export default DL2;








